/**
 * DashboardPdvDesktopService
 *
 * Agrega os dados necessários para o Dashboard do PDV Desktop.
 * Usa queries leves (COUNT, GROUP BY) — não carrega coleções completas.
 */
import { getElectronAPI } from "@/plugins/desktop";

function _getSync(controles, entidade) {
  const item = (controles || []).find((c) => c.entidade === entidade);
  return item ? item.ultimo_sync : null;
}

const DashboardPdvDesktopService = {
  /**
   * Retorna os totais de resumo (produtos, clientes, nfces, pendentes, caixa).
   */
  async obterResumo() {
    const api = getElectronAPI();
    if (!api) throw new Error("Electron API não disponível.");

    const [
      produtos,
      clientes,
      nfces,
      pendentesTransmissao,
      controles,
      caixaPendentes,
    ] = await Promise.all([
      api.produtos.contarProdutos(),
      api.clientes.contarClientes(),
      api.nfce.contarNfce(),
      api.nfce.contarPendentesTransmissao(),
      api.sync.getControle(),
      api.caixa.buscarPendentesSync(),
    ]);

    const ultimaSync =
      _getSync(controles, "produtos") ||
      _getSync(controles, "clientes") ||
      _getSync(controles, "formas_pagamento") ||
      null;

    return {
      produtos_total:         produtos              ?? 0,
      clientes_total:         clientes              ?? 0,
      nfces_total:            nfces                 ?? 0,
      vendas_pendentes:       pendentesTransmissao  ?? 0,
      caixa_pendente:         Array.isArray(caixaPendentes) ? caixaPendentes.length : 0,
      ultima_sincronizacao:   ultimaSync,
      controles:              controles || [],
    };
  },

  /**
   * Retorna os produtos mais vendidos nos últimos 7 dias.
   * @returns {Promise<Array<{ descricao: string, id_produto: number, quantidade_total: number }>>}
   */
  async obterProdutosMaisVendidosSemana() {
    const api = getElectronAPI();
    if (!api) return [];

    const dataInicio = new Date();
    dataInicio.setDate(dataInicio.getDate() - 7);
    const dataInicioStr = dataInicio.toISOString().slice(0, 19).replace("T", " ");

    const rows = await api.dashboard.produtosMaisVendidos(dataInicioStr);
    return Array.isArray(rows) ? rows : [];
  },

  /**
   * Retorna os grupos/categorias mais vendidos nos últimos 7 dias.
   * @returns {Promise<Array<{ grupo_descricao: string, id_grupo: number, quantidade_total: number, valor_total: number }>>}
   */
  async obterVendasPorGrupoSemana() {
    const api = getElectronAPI();
    if (!api) return [];

    const dataInicio = new Date();
    dataInicio.setDate(dataInicio.getDate() - 7);
    const dataInicioStr = dataInicio.toISOString().slice(0, 19).replace("T", " ");

    const rows = await api.dashboard.vendasPorGrupo(dataInicioStr);
    return Array.isArray(rows) ? rows : [];
  },

  /**
   * Retorna as informações do terminal, emitente, configuração NFC-e e impressora.
   */
  async obterDadosTerminal() {
    const api = getElectronAPI();
    if (!api) return {};

    const [info, emitente, configuracao, impressora] = await Promise.all([
      api.terminal.getInfo(),
      api.emitente.buscarEmitente(),
      api.configuracaoNfce.buscarConfiguracao(),
      api.impressoraConfig.buscar(),
    ]);

    return { info: info || {}, emitente: emitente || null, configuracao: configuracao || null, impressora: impressora || null };
  },
};

export default DashboardPdvDesktopService;
